// Enhanced Fuel Transactions Service - Solution 1 Implementation
// This replaces the existing fuel-transactions.js service with transaction-safe deletion

import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDocs, 
  getDoc,
  query, 
  where, 
  orderBy, 
  limit,
  Timestamp,
  writeBatch,
  runTransaction
} from 'firebase/firestore';
import { db } from '../../firebase';

class EnhancedFuelTransactionsService {
  constructor() {
    this.collectionName = 'fuel_transactions';
  }

  // ENHANCED: Transaction-safe deletion method
  async deleteFuelTransaction(transactionId) {
    console.log(`🗑️ Starting transaction-safe deletion for: ${transactionId}`);
    
    try {
      // Use Firestore transaction to ensure atomicity
      const result = await runTransaction(db, async (transaction) => {
        // Step 1: Get the fuel transaction document
        const transactionDocRef = doc(db, this.collectionName, transactionId);
        const transactionDoc = await transaction.get(transactionDocRef);
        
        if (!transactionDoc.exists()) {
          console.log(`⚠️ Transaction ${transactionId} doesn't exist`);
          return { success: true, skipped: true, reason: 'Transaction not found' };
        }

        const transactionData = transactionDoc.data();
        console.log(`📄 Found transaction: ${transactionData.invoice_number || 'N/A'} - ${transactionData.cost || 0} SAR`);

        // Step 2: Find all costings that reference this transaction
        console.log('🔍 Searching for costings that reference this transaction...');
        const costingsSnapshot = await getDocs(
          query(collection(db, 'shipmentCostings'), where('companyId', '==', transactionData.companyId))
        );

        const costingsToUpdate = [];
        let totalAmountRemoved = 0;

        // Step 3: Prepare all costing updates
        for (const costingDoc of costingsSnapshot.docs) {
          const costingData = costingDoc.data();
          
          if (costingData.appliedFuelTransactions && Array.isArray(costingData.appliedFuelTransactions)) {
            const transactionIndex = costingData.appliedFuelTransactions.findIndex(
              tx => tx.id === transactionId
            );

            if (transactionIndex !== -1) {
              console.log(`🎯 Found in costing ${costingDoc.id} (shipment: ${costingData.shipmentId})`);
              
              // Remove the transaction from applied list
              const updatedTransactions = [...costingData.appliedFuelTransactions];
              const removedTransaction = updatedTransactions.splice(transactionIndex, 1)[0];
              totalAmountRemoved += parseFloat(removedTransaction.cost || 0);

              // Recalculate amounts
              const newPetroAppAmount = updatedTransactions.reduce((sum, tx) => sum + (parseFloat(tx.cost) || 0), 0);
              const manualFuelAmount = parseFloat(costingData.manualFuelAmount || 0);
              const newTotalFuelAmount = newPetroAppAmount + manualFuelAmount;

              // Calculate VAT breakdown
              const petroAppVAT = this.calculateVATBreakdown(newPetroAppAmount, true);
              const manualVAT = this.calculateVATBreakdown(manualFuelAmount, costingData.manualFuelIncludesVAT !== false);
              
              const totalVAT = petroAppVAT.vatAmount + manualVAT.vatAmount;
              const totalBaseAmount = petroAppVAT.baseAmount + manualVAT.baseAmount;

              // Prepare update data
              const updateData = {
                appliedFuelTransactions: updatedTransactions,
                petroAppFuelAmount: newPetroAppAmount,
                totalFuelAmount: newTotalFuelAmount,
                fuelVATAmount: totalVAT,
                fuelBaseAmount: totalBaseAmount,
                
                // Legacy fields for backward compatibility
                fuel: newTotalFuelAmount,
                fuelAmount: newTotalFuelAmount,
                fuelBaseAmount: totalBaseAmount,
                fuelVATAmount: totalVAT,
                
                // Reset fields if no fuel transactions remain
                ...(newTotalFuelAmount === 0 && {
                  petroAppFuelBaseAmount: 0,
                  petroAppFuelVATAmount: 0,
                  totalFuelBaseAmount: 0,
                  totalFuelVATAmount: 0
                }),
                
                lastUpdated: new Date(),
                transactionRemovedAt: new Date(),
                removedTransactionId: transactionId,
                removedTransactionAmount: removedTransaction.cost
              };

              costingsToUpdate.push({
                docRef: costingDoc.ref,
                updateData,
                costingId: costingDoc.id,
                shipmentId: costingData.shipmentId,
                removedAmount: removedTransaction.cost
              });
            }
          }
        }

        console.log(`📊 Will update ${costingsToUpdate.length} costings, removing ${totalAmountRemoved.toFixed(2)} SAR total`);

        // Step 4: Apply all updates in the transaction
        costingsToUpdate.forEach(({ docRef, updateData }) => {
          transaction.update(docRef, updateData);
        });

        // Step 5: Clear shipment assignment if exists
        if (transactionData.shipment_id || transactionData.matched) {
          console.log('🔄 Clearing shipment assignment...');
          transaction.update(transactionDocRef, {
            shipment_id: null,
            matched: false,
            assignment_cleared_at: new Date(),
            assignment_cleared_reason: 'deletion_process'
          });
        }

        // Step 6: Delete the transaction
        console.log('🗑️ Deleting transaction from database...');
        transaction.delete(transactionDocRef);

        return {
          success: true,
          deletedTransactionId: transactionId,
          costingsUpdated: costingsToUpdate.length,
          totalAmountRemoved,
          updatedCostings: costingsToUpdate.map(c => ({
            costingId: c.costingId,
            shipmentId: c.shipmentId,
            removedAmount: c.removedAmount
          }))
        };
      });

      if (result.success) {
        console.log(`✅ Transaction ${transactionId} deleted successfully!`);
        if (!result.skipped) {
          console.log(`📊 Summary: Updated ${result.costingsUpdated} costings, removed ${result.totalAmountRemoved.toFixed(2)} SAR`);
          result.updatedCostings.forEach(c => {
            console.log(`   - Costing ${c.costingId} (${c.shipmentId}): -${c.removedAmount} SAR`);
          });
        }
      }

      return result;

    } catch (error) {
      console.error(`❌ Transaction-safe deletion failed for ${transactionId}:`, error);
      throw error;
    }
  }

  // ENHANCED: Batch deletion with transaction safety
  async deleteFuelTransactionsBatch(companyId, transactionIds) {
    console.log(`🗑️ Starting batch deletion of ${transactionIds.length} transactions`);
    
    const results = {
      successful: [],
      failed: [],
      skipped: [],
      totalCostingsUpdated: 0,
      totalAmountRemoved: 0,
      errors: []
    };

    // Process transactions one by one to ensure each is atomic
    for (let i = 0; i < transactionIds.length; i++) {
      const transactionId = transactionIds[i];
      console.log(`🔄 Processing ${i + 1}/${transactionIds.length}: ${transactionId}`);
      
      try {
        const result = await this.deleteFuelTransaction(transactionId);
        
        if (result.success) {
          if (result.skipped) {
            results.skipped.push(result);
            console.log(`⚠️ Skipped ${transactionId}: ${result.reason}`);
          } else {
            results.successful.push(result);
            results.totalCostingsUpdated += result.costingsUpdated || 0;
            results.totalAmountRemoved += result.totalAmountRemoved || 0;
            console.log(`✅ Deleted ${transactionId} successfully`);
          }
        }
      } catch (error) {
        console.error(`❌ Failed to delete ${transactionId}:`, error);
        results.failed.push({
          transactionId,
          error: error.message
        });
        results.errors.push(`${transactionId}: ${error.message}`);
      }
    }

    console.log(`\n📊 Batch deletion completed:`);
    console.log(`   ✅ Successful: ${results.successful.length}`);
    console.log(`   ⚠️ Skipped: ${results.skipped.length}`);
    console.log(`   ❌ Failed: ${results.failed.length}`);
    console.log(`   💰 Total amount removed: ${results.totalAmountRemoved.toFixed(2)} SAR`);
    console.log(`   📋 Costings updated: ${results.totalCostingsUpdated}`);

    if (results.errors.length > 0) {
      console.log(`\n❌ Errors encountered:`);
      results.errors.forEach(error => console.log(`   - ${error}`));
    }

    return results;
  }

  // ENHANCED: Remove fuel transaction from costing (now with transaction safety)
  async removeFuelTransactionFromCosting(companyId, transactionId) {
    console.log(`🔄 Removing transaction ${transactionId} from costings (transaction-safe)`);
    
    try {
      // This now uses the enhanced deletion method which handles costing cleanup
      // We'll just mark the transaction for removal from costings without deleting it
      const result = await runTransaction(db, async (transaction) => {
        // Get the fuel transaction
        const transactionDocRef = doc(db, this.collectionName, transactionId);
        const transactionDoc = await transaction.get(transactionDocRef);
        
        if (!transactionDoc.exists()) {
          console.log(`⚠️ Transaction ${transactionId} doesn't exist`);
          return false;
        }

        const transactionData = transactionDoc.data();
        
        // Find and update costings
        const costingsSnapshot = await getDocs(
          query(collection(db, 'shipmentCostings'), where('companyId', '==', companyId))
        );

        let found = false;
        
        for (const costingDoc of costingsSnapshot.docs) {
          const costingData = costingDoc.data();
          
          if (costingData.appliedFuelTransactions && Array.isArray(costingData.appliedFuelTransactions)) {
            const transactionIndex = costingData.appliedFuelTransactions.findIndex(
              tx => tx.id === transactionId
            );

            if (transactionIndex !== -1) {
              found = true;
              
              // Remove the transaction from applied list
              const updatedTransactions = [...costingData.appliedFuelTransactions];
              const removedTransaction = updatedTransactions.splice(transactionIndex, 1)[0];

              // Recalculate amounts
              const newPetroAppAmount = updatedTransactions.reduce((sum, tx) => sum + (parseFloat(tx.cost) || 0), 0);
              const manualFuelAmount = parseFloat(costingData.manualFuelAmount || 0);
              const newTotalFuelAmount = newPetroAppAmount + manualFuelAmount;

              // Calculate VAT breakdown
              const petroAppVAT = this.calculateVATBreakdown(newPetroAppAmount, true);
              const manualVAT = this.calculateVATBreakdown(manualFuelAmount, costingData.manualFuelIncludesVAT !== false);
              
              const totalVAT = petroAppVAT.vatAmount + manualVAT.vatAmount;
              const totalBaseAmount = petroAppVAT.baseAmount + manualVAT.baseAmount;

              const updateData = {
                appliedFuelTransactions: updatedTransactions,
                petroAppFuelAmount: newPetroAppAmount,
                totalFuelAmount: newTotalFuelAmount,
                fuelVATAmount: totalVAT,
                fuelBaseAmount: totalBaseAmount,
                lastUpdated: new Date()
              };

              transaction.update(costingDoc.ref, updateData);
              console.log(`✅ Removed from costing ${costingDoc.id}`);
            }
          }
        }

        // Clear shipment assignment
        if (transactionData.shipment_id || transactionData.matched) {
          transaction.update(transactionDocRef, {
            shipment_id: null,
            matched: false
          });
        }

        return found;
      });

      console.log(result ? '✅ Transaction removed from costings' : '⚠️ Transaction not found in any costings');
      return result;

    } catch (error) {
      console.error('❌ Error removing fuel transaction from costing:', error);
      throw error;
    }
  }

  // Helper method for VAT calculation
  calculateVATBreakdown(amount, includesVAT) {
    if (includesVAT) {
      const baseAmount = amount / 1.15;
      const vatAmount = amount - baseAmount;
      return { baseAmount, vatAmount, total: amount };
    } else {
      const vatAmount = amount * 0.15;
      const total = amount + vatAmount;
      return { baseAmount: amount, vatAmount, total };
    }
  }

  // =====================================
  // EXISTING METHODS (unchanged)
  // =====================================

  // Create a new fuel transaction
  async createFuelTransaction(companyId, transactionData) {
    try {
      const docRef = await addDoc(collection(db, this.collectionName), {
        companyId,
        ...transactionData,
        created_at: Timestamp.now(),
        updated_at: Timestamp.now()
      });
      
      return { id: docRef.id, companyId, ...transactionData };
    } catch (error) {
      console.error('Error creating fuel transaction:', error);
      throw error;
    }
  }

  // Update fuel transaction
  async updateFuelTransaction(transactionId, updateData) {
    try {
      const docRef = doc(db, this.collectionName, transactionId);
      await updateDoc(docRef, {
        ...updateData,
        updated_at: Timestamp.now()
      });
      
      return { id: transactionId, ...updateData };
    } catch (error) {
      console.error('Error updating fuel transaction:', error);
      throw error;
    }
  }

  // Get fuel transaction by ID
  async getFuelTransactionById(transactionId) {
    try {
      const docRef = doc(db, this.collectionName, transactionId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      } else {
        return null;
      }
    } catch (error) {
      console.error('Error getting fuel transaction by ID:', error);
      throw error;
    }
  }

  // Get fuel transactions for a company
  async getFuelTransactions(companyId, queryOptions = {}) {
    try {
      let transactionsQuery = query(
        collection(db, this.collectionName),
        where('companyId', '==', companyId)
      );

      if (queryOptions.orderBy) {
        transactionsQuery = query(transactionsQuery, orderBy(queryOptions.orderBy, queryOptions.orderDirection || 'desc'));
      }

      if (queryOptions.limit) {
        transactionsQuery = query(transactionsQuery, limit(queryOptions.limit));
      }

      const querySnapshot = await getDocs(transactionsQuery);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting fuel transactions:', error);
      throw error;
    }
  }

  // MISSING METHOD: Get fuel transactions by company (alias for getFuelTransactions)
  async getFuelTransactionsByCompany(companyId, queryOptions = {}) {
    return this.getFuelTransactions(companyId, queryOptions);
  }

  // MISSING METHOD: Get fuel transactions by shipment
  async getFuelTransactionsByShipment(companyId, shipmentId) {
    try {
      const transactionsQuery = query(
        collection(db, this.collectionName),
        where('companyId', '==', companyId),
        where('shipment_id', '==', shipmentId)
      );

      const querySnapshot = await getDocs(transactionsQuery);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting fuel transactions by shipment:', error);
      throw error;
    }
  }

  // MISSING METHOD: Batch assign to shipment
  async batchAssignToShipment(transactionIds, shipmentId) {
    try {
      const batch = writeBatch(db);
      
      transactionIds.forEach(transactionId => {
        const docRef = doc(db, this.collectionName, transactionId);
        batch.update(docRef, {
          shipment_id: shipmentId,
          matched: true,
          updated_at: Timestamp.now()
        });
      });

      await batch.commit();
      console.log(`✅ Assigned ${transactionIds.length} transactions to shipment ${shipmentId}`);
      return true;
    } catch (error) {
      console.error('Error batch assigning to shipment:', error);
      throw error;
    }
  }

  // MISSING METHOD: Bulk update fuel transactions
  async bulkUpdateFuelTransactions(updates) {
    try {
      const batch = writeBatch(db);
      
      updates.forEach(({ id, updateData }) => {
        const docRef = doc(db, this.collectionName, id);
        batch.update(docRef, {
          ...updateData,
          updated_at: Timestamp.now()
        });
      });

      await batch.commit();
      console.log(`✅ Updated ${updates.length} fuel transactions`);
      return { success: updates.length, failed: 0 };
    } catch (error) {
      console.error('Error bulk updating fuel transactions:', error);
      throw error;
    }
  }

  // MISSING METHOD: Check for duplicates
  async checkForDuplicates(companyId, transactions) {
    try {
      const existingTransactions = await this.getFuelTransactionsByCompany(companyId);
      const duplicates = [];
      
      transactions.forEach(newTx => {
        const duplicate = existingTransactions.find(existing => 
          existing.invoice_number === newTx.invoice_number &&
          existing.fill_time === newTx.fill_time
        );
        
        if (duplicate) {
          duplicates.push({
            newTransaction: newTx,
            existingTransaction: duplicate
          });
        }
      });
      
      return duplicates;
    } catch (error) {
      console.error('Error checking for duplicates:', error);
      throw error;
    }
  }

  // Bulk create fuel transactions
  async bulkCreateFuelTransactions(companyId, transactions) {
    try {
      const batch = writeBatch(db);
      const createdTransactions = [];

      transactions.forEach(transactionData => {
        const docRef = doc(collection(db, this.collectionName));
        const fullTransactionData = {
          companyId,
          ...transactionData,
          created_at: Timestamp.now(),
          updated_at: Timestamp.now()
        };
        
        batch.set(docRef, fullTransactionData);
        createdTransactions.push({ id: docRef.id, ...fullTransactionData });
      });

      await batch.commit();
      console.log(`✅ Created ${transactions.length} fuel transactions`);
      return createdTransactions;
    } catch (error) {
      console.error('Error bulk creating fuel transactions:', error);
      throw error;
    }
  }

  // MISSING METHOD: Batch import fuel transactions (alias for bulkCreateFuelTransactions)
  async batchImportFuelTransactions(companyId, transactions, importBatchId, userId) {
    try {
      console.log(`🔄 Batch importing ${transactions.length} fuel transactions`);
      
      // Add import metadata to each transaction
      const transactionsWithMetadata = transactions.map(tx => ({
        ...tx,
        import_batch_id: importBatchId,
        imported_by: userId,
        imported_at: Timestamp.now(),
        // Ensure backward compatibility with existing structure
        status: tx.status || 'imported'
      }));

      // Use the existing bulk create method
      const result = await this.bulkCreateFuelTransactions(companyId, transactionsWithMetadata);
      
      console.log(`✅ Batch import completed: ${result.length} transactions imported`);
      return result;
    } catch (error) {
      console.error('Error batch importing fuel transactions:', error);
      throw error;
    }
  }


}

// Export the enhanced service
export const fuelTransactionsService = new EnhancedFuelTransactionsService();